import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

const FormSchema = z.object({
  priority: z.coerce.number().optional(),
  calculation_start_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  run_at: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export function DelayedJobsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                DelayedJobs {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="priority"
                    label={t(`${table_name}.columns.priority`, {
                      ns: "tables",
                      defaultValue: "Priority",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <DatePickerField
                    name="calculation_start_date"
                    label={t(`${table_name}.columns.calculation_start_date`, {
                      ns: "tables",
                      defaultValue: "Calculation Start Date",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-1 gap-4">
                  <DatePickerField
                    name="run_at"
                    label={t(`${table_name}.columns.run_at`, {
                      ns: "tables",
                      defaultValue: "Run At",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
